import {
  Box,
  Button,
  ColumnDatas,
  DropdownBody,
  DropdownItem,
  getColor,
  Icons,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "fds";
import { useCallback, useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import useAdminContentStore from "../../redux/dispatcher/useAdminContentStore";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import dayjs from "dayjs";
import { DocInfo, DocumentListReq } from "../../types/files";
import { Workgroup } from "../../types/workgroup";
import utilsCommon from "../../shared/utils/common";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import AdminFilter from "../../components/AdminFilter";
import { FilterData } from "../../types/adminFilter";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";
import utilsDocs from "../../shared/utils/docs";

const Trash: React.FC = () => {
  const { adminContentStore, getContentList, deleteDocNow, restoreDoc } =
    useAdminContentStore();
  const { adminWorkgroupStore, getWorkgroupList } = useAdminWorkgroupStore();
  const checkboxGroup = useCheckboxGroup<string>();
  const { t } = useTranslation();

  /**** State 모음 ****/
  const [trashMenu, setTrashMenu] = useState<{
    x: number;
    y: number;
    target: DocInfo;
  }>({
    x: 0,
    y: 0,
    target: {} as DocInfo,
  });
  const inputRef = useRef<HTMLTableRowElement>(null);

  const initFilterData = {
    userId: "",
    fileName: "",
    workgroupIds: [],
    dateFilter: "1month",
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().endOf("day").valueOf(),
    },
    order: "datedesc",
  } as FilterData;

  const [filterData, setFilterData] = useState<FilterData>(initFilterData);
  const [files, setFiles] = useState([] as DocInfo[]);
  const [fileTotal, setTotal] = useState(0);
  const pageCount = 50;
  const [nextPage, setNextPage] = useState(-1);
  const [workgroupList, setWorkgroupList] = useState([] as Workgroup[]);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const tableRef = useRef<HTMLDivElement>(null);
  const [initDone, setInitDone] = useState(false);

  /**** UseEffect 모음 ****/
  useEffect(() => {
    getTrashFileList(0);
    getWorkgroupList({
      orderBy: "workgroupName",
      orderByType: "ASC",
      status: -1,
      posPage: 1,
      pageRange: 1,
      rowRange: 0,
      searchDate: {
        start: 0,
        end: 0,
      },
      workgroupName: "",
      managerId: "",
      isDetail: "",
    });
    setInitDone(true);
  }, []);

  useEffect(() => {
    if (adminContentStore.contentList?.pages) {
      if (adminContentStore.contentList.pages.page === 0) {
        setFiles(adminContentStore.contentList.docs.doc);
      } else {
        setFiles(files.concat(adminContentStore.contentList.docs.doc));
      }
      setTotal(adminContentStore.contentList.total);
      setNextPage(adminContentStore.contentList.pages.nextPage);
    }
  }, [adminContentStore.contentList]);

  useEffect(() => {
    if (adminWorkgroupStore.usingWorkgroup.workgroupList?.length > 0) {
      setWorkgroupList(adminWorkgroupStore.usingWorkgroup.workgroupList);
    }
  }, [adminWorkgroupStore.usingWorkgroup.workgroupList]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    initDone && getTrashFileList(0);
  }, [filterData]);

  const handleTurnOffSettng = (): void => {
    setTrashMenu({
      x: 0,
      y: 0,
      target: {} as DocInfo,
    });
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  const getTrashFileList = (page?: number) => {
    if (page !== -1) {
      getContentList({
        page: page,
        size: pageCount,
        order: filterData.order,
        search: filterData.fileName,
        readType: "trash",
        scopeType: "all",
        masterUserId: filterData.userId,
        tagCodes: filterData.workgroupIds ?? [],
        date: {
          start: filterData?.date?.start,
          end: filterData?.date?.end,
        },
        folderCode: "",
      } as DocumentListReq);
    }
  };

  const countPermanentlyDeleteDay = (revisionDate: number) => {
    const today = Date.now();
    const remainDay = Math.ceil(30 - (today - revisionDate) / 86400000);
    return remainDay;
  };

  const restoreDocFunc = async (syncId: string) => {
    await restoreDoc([syncId]);
    getTrashFileList(0);
  };

  const deleteDocNowFunc = async (syncId: string) => {
    await deleteDocNow([syncId]);
    getTrashFileList(0);
  };

  const restoreDocsFunc = async () => {
    await restoreDoc(checkboxGroup.selected);
    checkboxGroup.handleAllClick([]);
    getTrashFileList(0);
  };

  const deleteDocsNowFunc = async () => {
    await deleteDocNow(checkboxGroup.selected);
    checkboxGroup.handleAllClick([]);
    getTrashFileList(0);
  };

  const renderTrashMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{ x: target.x, y: target.y }}
      >
        <DropdownItem
          label={t("admin.trash.recovery")}
          onClick={() => {
            restoreDocFunc(target.syncId);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.trash.hardDelete")}
          onClick={() => {
            deleteDocNowFunc(target.syncId);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  function transformFileList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: (
                <Box alignItems="center" width={"100%"}>
                  {utilsDocs.renderDocIcon(item.documnetName, 24)}
                  <Tooltip
                    message={item.contentName}
                    maxWidth={"calc(100% - 80px)"}
                  >
                    <Typography classes={"mg ml-4"} ellipsis>
                      {item.contentName}
                    </Typography>
                  </Tooltip>

                  <Tag
                    type="box"
                    backgroundColor={getColor("COLOR_BG_INTERACTIVE_ERROR")}
                    textColor={getColor("COLOR_TEXT_ERROR")}
                    classes="mg ml-10"
                    text={`D-` + countPermanentlyDeleteDay(item.deleteDate)}
                  />
                </Box>
              ),
            },
            {
              columnText:
                item.workgroup.workgroupId === "NONEWORKGROUP"
                  ? "My Box"
                  : item.workgroup.workgroupName,
            },
            {
              columnText: item.revisionNo,
            },
            {
              columnText: utilsCommon.getNameWithId(
                item.masterUser.userName,
                item.masterUser.userId
              ),
            },
            {
              columnText: utilsCommon.renderDateFormat(item.deleteDate),
            },
          ],
          id: item.syncId,
          checkBoxClick: () => {
            checkboxGroup.handleClick(item.syncId);
          },
          hover: (
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTrashMenu({
                  x: e.pageX,
                  y: e.pageY,
                  target: item,
                });
              }}
              btnStyle="icon"
              type="primary"
              size="sm"
              leadingIcon={<Icons variant="line" label="moreVertical" />}
            />
          ),
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  return (
    <Box classes="adminTrash" direction="column">
      {trashMenu.x > 0 && trashMenu.y > 0 && renderTrashMenuButton(trashMenu)}
      <Box classes="adminTrash-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.trash")}</Typography>
      </Box>
      <Box classes="adminTrash-title" justifyContent="space-between">
        <AdminFilter
          initFilterData={initFilterData}
          filterData={filterData}
          setFilterData={setFilterData}
          workgroupList={workgroupList}
        />
        {checkboxGroup.selected.length >= 1 && (
          <Box>
            <Button
              onClick={() => {
                restoreDocsFunc();
              }}
              type="primary"
              btnStyle="link"
              label={t("admin.trash.recovery")}
              state={checkboxGroup.selected.length < 1 ? "disable" : "default"}
              classes="mg ml-8"
              leadingIcon={<Icons variant="line" label="redoLeft" />}
            />
            <Button
              onClick={() => {
                deleteDocsNowFunc();
              }}
              type="error"
              btnStyle="link"
              label={t("admin.trash.hardDelete")}
              state={checkboxGroup.selected.length < 1 ? "disable" : "default"}
              classes="mg ml-8"
              leadingIcon={<Icons variant="line" label="delete" />}
            />
          </Box>
        )}
      </Box>

      <Box height={44} minHeight={44}>
        {checkboxGroup.selected.length === 0 ? (
          <Box alignItems="center">
            <Typography type="subtitle2">
              {t("admin.common.label.document")}
            </Typography>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_BRAND")}
              classes={"mg ml-4"}
            >
              {fileTotal}
            </Typography>
          </Box>
        ) : (
          <Box alignItems="center">
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_BRAND")}
            >
              {checkboxGroup.selected.length}
            </Typography>
            <Typography type="subtitle2">
              {t("admin.common.label.selected")}
            </Typography>
          </Box>
        )}
      </Box>
      <Box ref={tableRef} classes="mg mt-12" direction="column">
        <Table
          width={"100%"}
          height={(files.length === 0 ? 0 : height) + "px"}
          header={[
            {
              headerText: t("admin.trash.fileName"),
              width: "calc(100% - 850px)",
              order:
                filterData.order === "filedesc"
                  ? "downOn"
                  : filterData.order === "file"
                  ? "upOn"
                  : "downOff",
              orderAction: () => {
                filterData.order === "filedesc"
                  ? setFilterData({
                      ...filterData,
                      order: "file",
                    })
                  : filterData.order === "file"
                  ? setFilterData({
                      ...filterData,
                      order: "filedesc",
                    })
                  : setFilterData({
                      ...filterData,
                      order: "filedesc",
                    });
              },
            },
            {
              headerText: t("common.location"),
              width: "300px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.trash.version"),
              width: "100px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.trash.deleteUser"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.trash.deleteDate"),
              width: "200px",
              order:
                filterData.order === "datedesc"
                  ? "downOn"
                  : filterData.order === "date"
                  ? "upOn"
                  : "downOff",
              orderAction: () => {
                filterData.order === "datedesc"
                  ? setFilterData({
                      ...filterData,
                      order: "date",
                    })
                  : filterData.order === "date"
                  ? setFilterData({
                      ...filterData,
                      order: "datedesc",
                    })
                  : setFilterData({
                      ...filterData,
                      order: "datedesc",
                    });
              },
            },
          ]}
          data={transformFileList(files)}
          totalCount={files.length}
          clickIds={checkboxGroup.selected.map((index) => String(index))}
          checkBoxClick={() =>
            checkboxGroup.handleAllClick(
              checkboxGroup.selected.length === files.length
                ? []
                : files.map((q) => q.syncId + "")
            )
          }
          endReached={() => {
            getTrashFileList(nextPage);
          }}
          columnHeight="md"
        />
        {files.length === 0 && <EmptyFilterPage />}
      </Box>
    </Box>
  );
};

export default Trash;
